import type { MultiSelectItem } from '@/components/ui/multi-select';
import { formatCount } from '@/lib/utils';

import { NO_TAG_TEXT } from './constants';
import type {
    QueueGroup,
    T360ClusteringRunState,
    T360ClusterDocument,
    T360ClusterGroup,
    T360ClusterLabelStatus,
    T360ClusterLeaf,
    T360ClusterTree,
    T360DocClass,
    T360DocumentTags,
    T360LeafTag,
    T360TagConfig,
} from './types';

const SAMPLE_SIZE = 5;
const MULTI_VALUE_SELECT = '0-*';
export const MAIN_TAG_KEY = 'main';

const NO_TAG_VALUE = 'none';

export const LABEL_STATUS_LABELS: Record<T360ClusterLabelStatus, string> = {
    to_review: 'To review',
    confirmed: 'Confirmed',
};

export const LABEL_FILTERS = ['to_review', 'confirmed'] as const;
export type LabelFilter = (typeof LABEL_FILTERS)[number];
export type LabelsView = 'review' | 'no-documents' | 'running' | 'failed' | 'not-clustered';

export const RUN_STATE_LABELS: Record<T360ClusteringRunState, string> = {
    queued: 'Queued',
    running: 'Grouping documents',
    naming: 'Naming groups',
    done: 'Done',
    failed: 'Failed',
};

export type LeafTagState = 'tagged' | 'none' | 'suggesting' | 'unsuggested' | 'skipped';

export const LEAF_TAG_STATE_TEXTS: Record<Exclude<LeafTagState, 'tagged'>, string> = {
    none: 'No tag',
    suggesting: 'Suggesting…',
    unsuggested: 'No suggestion',
    skipped: 'Not suggested',
};

export interface TagOption {
    tag: T360LeafTag;
    name: string;
    /** 'main' for types, otherwise the facet key. */
    groupKey: string;
}

export function isLeafConfirmed(leaf: T360ClusterLeaf): boolean {
    return leaf.tagsConfirmedAt !== null;
}

function resolveLeafStatus(leaf: T360ClusterLeaf): T360ClusterLabelStatus {
    return isLeafConfirmed(leaf) ? 'confirmed' : 'to_review';
}

function isMainTag(tag: T360LeafTag): boolean {
    return tag.key === MAIN_TAG_KEY;
}

export function mainCodes(tags: T360LeafTag[]): string[] {
    return tags.filter((tag) => isMainTag(tag) && tag.value !== '').map((tag) => tag.value);
}

export function facetTags(tags: T360LeafTag[]): T360LeafTag[] {
    return tags.filter((tag) => !isMainTag(tag));
}

function isSameTag(a: T360LeafTag, b: T360LeafTag): boolean {
    return a.key === b.key && a.value === b.value;
}

function hasTag(tags: T360LeafTag[], tag: T360LeafTag): boolean {
    return tags.some((item) => isSameTag(item, tag));
}

export function isSameTagSet(a: T360LeafTag[], b: T360LeafTag[]): boolean {
    return a.length === b.length && a.every((tag) => hasTag(b, tag)) && b.every((tag) => hasTag(a, tag));
}

export function resolveLeafTags(leaf: T360ClusterLeaf): T360LeafTag[] {
    return isLeafConfirmed(leaf) ? leaf.tags : leaf.suggestedTags.filter((tag) => tag.value !== '');
}

export function sortTags(config: T360TagConfig, tags: T360LeafTag[]): T360LeafTag[] {
    const rank = (tag: T360LeafTag): number => {
        const index = config.facets.findIndex((facet) => facet.key === tag.key);
        return index === -1 ? config.facets.length : index;
    };
    return [
        ...tags.filter(isMainTag),
        ...facetTags(tags).sort((a, b) => rank(a) - rank(b) || a.value.localeCompare(b.value)),
    ];
}

export function canCombineMains(config: T360TagConfig, codes: string[]): boolean {
    const unique = [...new Set(codes)];
    return unique.length <= 1 || config.combined.some((group) => unique.every((code) => group.includes(code)));
}

/** Codes the config reads together become one unit; every other code stands alone. */
export function groupCombinedMains(config: T360TagConfig, codes: string[]): string[][] {
    const left = [...codes];
    const units: string[][] = [];
    for (const group of config.combined) {
        const held = left.filter((code) => group.includes(code));
        if (held.length !== group.length) continue;
        units.push(held);
        for (const code of held) left.splice(left.indexOf(code), 1);
    }
    return [...units, ...left.map((code) => [code])];
}

function listFacetRules(config: T360TagConfig, codes: string[]): T360DocClass['facets'] {
    return codes.flatMap((code) => config.classes.find((item) => item.code === code)?.facets ?? []);
}

function isFacetAllowed(config: T360TagConfig, codes: string[], key: string): boolean {
    return codes.length === 0 || listFacetRules(config, codes).some((rule) => rule.facet === key);
}

function isSingleValueFacet(config: T360TagConfig, codes: string[], key: string): boolean {
    const rules = listFacetRules(config, codes).filter((rule) => rule.facet === key);
    if (rules.length > 0) return rules.every((rule) => rule.select !== MULTI_VALUE_SELECT);
    return config.facets.find((facet) => facet.key === key)?.select !== MULTI_VALUE_SELECT;
}

export function addTag(config: T360TagConfig, tags: T360LeafTag[], tag: T360LeafTag): T360LeafTag[] {
    if (hasTag(tags, tag)) return tags;
    if (isMainTag(tag)) {
        const mains = tags.filter(isMainTag);
        const keptMains = canCombineMains(config, [...mainCodes(mains), tag.value]) ? mains : [];
        return sortTags(config, [...keptMains, tag, ...facetTags(tags)]);
    }
    const isSingle = isSingleValueFacet(config, mainCodes(tags), tag.key);
    const others = isSingle ? tags.filter((item) => item.key !== tag.key) : tags;
    return sortTags(config, [...others, tag]);
}

export function removeTag(tags: T360LeafTag[], tag: T360LeafTag): T360LeafTag[] {
    return tags.filter((item) => !isSameTag(item, tag));
}

export function buildTagOptions(config: T360TagConfig, tags: T360LeafTag[]): TagOption[] {
    const codes = mainCodes(tags);
    const mainOptions = config.classes
        .filter((item) => !codes.includes(item.code))
        .map((item) => ({ tag: { key: MAIN_TAG_KEY, value: item.code }, name: item.name, groupKey: MAIN_TAG_KEY }));
    const facetKeys = codes.length > 0
        ? [...new Set(listFacetRules(config, codes).map((rule) => rule.facet))]
        : config.facets.map((facet) => facet.key);
    const facetOptions = facetKeys.flatMap((key) => {
        const facet = config.facets.find((item) => item.key === key);
        return Object.entries(facet?.values ?? {})
            .map(([value, name]) => ({ tag: { key, value }, name, groupKey: key }))
            .filter((option) => !hasTag(tags, option.tag));
    });
    return [...mainOptions, ...facetOptions];
}

export function listDisallowedFacetKeys(config: T360TagConfig, tags: T360LeafTag[]): string[] {
    const codes = mainCodes(tags);
    const keys = facetTags(tags).map((tag) => tag.key).filter((key) => !isFacetAllowed(config, codes, key));
    return [...new Set(keys)];
}

export function listMissingFacetKeys(config: T360TagConfig, tags: T360LeafTag[]): string[] {
    const keys = listFacetRules(config, mainCodes(tags))
        .filter((rule) => rule.required && !tags.some((tag) => tag.key === rule.facet))
        .map((rule) => rule.facet);
    return [...new Set(keys)];
}

/** Single-value facets holding more than one value, which happens when a type that allowed several is removed. */
export function listConflictingFacetKeys(config: T360TagConfig, tags: T360LeafTag[]): string[] {
    const codes = mainCodes(tags);
    const keys = [...new Set(facetTags(tags).map((tag) => tag.key))];
    return keys.filter((key) =>
        isFacetAllowed(config, codes, key)
        && isSingleValueFacet(config, codes, key)
        && facetTags(tags).filter((tag) => tag.key === key).length > 1,
    );
}

export function resolveTagsToSave(config: T360TagConfig, tags: T360LeafTag[]): T360LeafTag[] {
    const keptCodes: string[] = [];
    for (const code of mainCodes(tags)) {
        if (!keptCodes.includes(code) && canCombineMains(config, [...keptCodes, code])) keptCodes.push(code);
    }
    let facets: T360LeafTag[] = [];
    for (const tag of facetTags(tags)) {
        if (tag.value === '' || !isFacetAllowed(config, keptCodes, tag.key)) continue;
        if (isSingleValueFacet(config, keptCodes, tag.key)) {
            facets = facets.filter((item) => item.key !== tag.key);
        }
        facets.push(tag);
    }
    const mains = keptCodes.map((value) => ({ key: MAIN_TAG_KEY, value }));
    return sortTags(config, [...mains, ...facets]);
}

function joinNames(names: string[]): string {
    return names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names.join('');
}

function formatClassNames(config: T360TagConfig, codes: string[]): string {
    return joinNames(codes.map((code) => docClassName(config, code)));
}

export function facetName(config: T360TagConfig, key: string): string {
    return config.facets.find((facet) => facet.key === key)?.name ?? key;
}

export function facetValueName(config: T360TagConfig, key: string, value: string): string {
    return config.facets.find((facet) => facet.key === key)?.values[value] ?? value;
}

export function formatFacetTag(config: T360TagConfig, tag: T360LeafTag): string {
    return `${facetName(config, tag.key)}: ${facetValueName(config, tag.key, tag.value)}`;
}

function formatFacetNames(config: T360TagConfig, keys: string[]): string {
    return joinNames(keys.map((key) => facetName(config, key)));
}

export function formatDisallowedFacetsWarning(config: T360TagConfig, tags: T360LeafTag[]): string {
    const codes = mainCodes(tags);
    const keys = listDisallowedFacetKeys(config, tags);
    return `${formatClassNames(config, codes)} ${codes.length > 1 ? 'do' : 'does'} not take `
        + `${formatFacetNames(config, keys)}. Saving drops ${keys.length > 1 ? 'them' : 'it'}.`;
}

export function formatConflictingFacetsWarning(config: T360TagConfig, tags: T360LeafTag[]): string {
    const codes = mainCodes(tags);
    const keys = listConflictingFacetKeys(config, tags);
    return `${formatClassNames(config, codes)} ${codes.length > 1 ? 'take' : 'takes'} one `
        + `${formatFacetNames(config, keys)}. Remove the extra ${keys.length > 1 ? 'ones' : 'one'}, or saving keeps the last.`;
}

export function formatMissingFacets(config: T360TagConfig, keys: string[]): string {
    return `Needs ${formatFacetNames(config, keys)}`;
}

export function formatInvalidCombination(config: T360TagConfig, codes: string[]): string {
    return `${formatClassNames(config, codes)} do not go on one document. Edit the tags to fix it.`;
}

export function resolveLeafTagState(
    leaf: T360ClusterLeaf,
    isOtherGroup: boolean,
    labelling: T360ClusterTree['labelling'],
): LeafTagState {
    if (mainCodes(resolveLeafTags(leaf)).length > 0) return 'tagged';
    if (isLeafConfirmed(leaf)) return 'none';
    if (leaf.suggestedTags.some((tag) => isMainTag(tag) && tag.value === '')) return 'none';
    if (isOtherGroup) return 'skipped';
    return labelling === 'pending' ? 'suggesting' : 'unsuggested';
}

export function buildLeafTagStates(
    queue: QueueGroup[],
    labelling: T360ClusterTree['labelling'],
): ReadonlyMap<number, LeafTagState> {
    return new Map(queue.flatMap(({ group, leaves }) =>
        leaves.map((leaf) => [leaf.id, resolveLeafTagState(leaf, group.isOther, labelling)] as const),
    ));
}

export function formatLeafTitle(group: T360ClusterGroup, leaf: T360ClusterLeaf): string {
    return group.leaves.length === 1 ? group.name : `${group.name} · ${leaf.name}`;
}

export function buildLeafQueue(groups: T360ClusterGroup[]): QueueGroup[] {
    return [...groups]
        .sort((a, b) => Number(a.isOther) - Number(b.isOther) || a.position - b.position)
        .map((group) => ({
            group,
            leaves: [...group.leaves].sort((a, b) => b.size - a.size || a.position - b.position),
        }));
}

export function filterLeafQueue(queue: QueueGroup[], filter: LabelFilter): QueueGroup[] {
    return queue.map(({ group, leaves }) => ({
        group, leaves: leaves.filter((leaf) => resolveLeafStatus(leaf) === filter),
    })).filter(({ leaves }) => leaves.length > 0);
}

export function flattenQueue(queue: QueueGroup[]): T360ClusterLeaf[] {
    return queue.flatMap(({ leaves }) => leaves);
}

export function countLeavesByStatus(queue: QueueGroup[]): Record<T360ClusterLabelStatus, number> {
    const counts = { to_review: 0, confirmed: 0 };
    for (const leaf of flattenQueue(queue)) counts[resolveLeafStatus(leaf)] += 1;
    return counts;
}

export function buildLabelProgress(queue: QueueGroup[]): {
    confirmedLeafCount: number;
    totalLeafCount: number;
    confirmedDocumentPercentage: number;
} {
    const leaves = flattenQueue(queue);
    const confirmedLeaves = leaves.filter((leaf) => resolveLeafStatus(leaf) === 'confirmed');
    const totalDocuments = leaves.reduce((sum, leaf) => sum + leaf.size, 0);
    const confirmedDocuments = confirmedLeaves.reduce((sum, leaf) => sum + leaf.size, 0);
    return {
        confirmedLeafCount: confirmedLeaves.length,
        totalLeafCount: leaves.length,
        confirmedDocumentPercentage: totalDocuments ? Math.round(100 * confirmedDocuments / totalDocuments) : 0,
    };
}

export function resolveInitialLeaf(queue: QueueGroup[]): T360ClusterLeaf | undefined {
    const leaves = flattenQueue(queue);
    return leaves.find((leaf) => resolveLeafStatus(leaf) === 'to_review') ?? leaves[0];
}

export function nextLeafToReview(queue: QueueGroup[], currentLeafId: number): T360ClusterLeaf | undefined {
    const leaves = flattenQueue(queue);
    const index = leaves.findIndex((leaf) => leaf.id === currentLeafId);
    return [...leaves.slice(index + 1), ...leaves.slice(0, index)]
        .find((leaf) => leaf.id !== currentLeafId && resolveLeafStatus(leaf) === 'to_review');
}

export function confirmLeafTags(
    tree: T360ClusterTree,
    {
        leafId,
        tags,
        config,
        confirmedAt,
    }: { leafId: number; tags: T360LeafTag[]; config: T360TagConfig; confirmedAt: string },
): T360ClusterTree {
    if (!tree.groups.some((group) => group.leaves.some((leaf) => leaf.id === leafId))) return tree;
    return {
        ...tree,
        groups: tree.groups.map((group) => ({
            ...group,
            leaves: group.leaves.map((leaf) => leaf.id !== leafId ? leaf : {
                ...leaf,
                tags: resolveTagsToSave(config, tags),
                tagsConfirmedAt: confirmedAt,
            }),
        })),
    };
}

export function resolveLabelsView(tree: T360ClusterTree): LabelsView {
    if (tree.run && tree.groups.some((group) => group.leaves.length > 0)) return 'review';
    if (tree.documentCount === 0) return 'no-documents';
    if (tree.liveRun) return 'running';
    if (tree.lastFailedRun) return 'failed';
    return 'not-clustered';
}

export function docClassName(config: T360TagConfig, code: string): string {
    return config.classes.find((item) => item.code === code)?.name ?? code;
}

export function formatPageCount(document: T360ClusterDocument): string {
    return typeof document.pageCount === 'number' ? formatCount(document.pageCount, 'page') : 'Pages unknown';
}

export function pickSamples(documents: T360ClusterDocument[], round: number, size = SAMPLE_SIZE): T360ClusterDocument[] {
    if (documents.length <= size) return documents;
    return Array.from({ length: size }, (_, index) => documents[(round * size + index) % documents.length]);
}

export function canPickMoreSamples(documents: T360ClusterDocument[], size = SAMPLE_SIZE): boolean {
    return documents.length > size;
}

export function resolveDocumentTags(
    base: T360DocumentTags | null,
    override: T360LeafTag[] | undefined,
): T360DocumentTags | null {
    if (override === undefined) return base;
    return { tags: override, status: 'confirmed', source: 'document' };
}

export function hasMatchingTag(documentTags: T360DocumentTags | null, filter: readonly string[]): boolean {
    if (filter.length === 0) return true;
    const codes = documentTags ? mainCodes(documentTags.tags) : [];
    if (codes.length === 0) return filter.includes(NO_TAG_VALUE);
    return codes.some((code) => filter.includes(code));
}

export function buildTagFilterItems(config: T360TagConfig): MultiSelectItem[] {
    return [
        { value: NO_TAG_VALUE, label: NO_TAG_TEXT },
        ...config.classes.map(({ code, name }) => ({ value: code, label: `${name} (${code})` })),
    ];
}

export function formatTagFilterSummary(filter: readonly string[], config: T360TagConfig): string {
    if (filter.length !== 1) return `${filter.length} tags`;
    return filter[0] === NO_TAG_VALUE ? NO_TAG_TEXT : docClassName(config, filter[0]);
}
